import get from "lodash-es/get";
import React, { Dispatch, SetStateAction } from "react";
import styled from "styled-components";

import {
  IMenuContentNormed,
  MenuDevice,
} from "../../../../../../../types/global";
import Hyperlink from "../../../../../../Hyperlink";

interface IProps {
  tab: IMenuContentNormed;
  device: MenuDevice;
  menu: IMenuContentNormed[];
  path: number[];
  setPath: Dispatch<SetStateAction<number[]>>;
}

const List = styled.ul`
  padding: 0;
  list-style: none;
  width: 100%;
`;

const Title = styled.li`
  color: #900f17;
  font-size: 14px;
  text-transform: uppercase;
  font-weight: bold;
  margin: 15px 0 8px 0;
  letter-spacing: 1px;
  border-bottom: 2px solid #dedede;
  padding-bottom: 4px;
  & a:hover {
    text-decoration: underline;
  }
`;

const NestedList = styled.li`
  border: 0 none;
  background: url(https://www.whisky.fr/skin/frontend/int/default/images/media/menu-li-puce.png)
    no-repeat scroll 0 center transparent;
  background-size: 4px 7px;
  padding: 0 0 0 10px;
  margin: 0 0 5px 0;
  width: 100%;
  height: auto;
  float: left;
  clear: both;
  text-align: left;
  margin-bottom: -1px;
  margin-top: 0px;

  & a {
    padding: 0;
    color: #4e4e4e;
    font-family: arial;
    font-size: 13px;
    text-decoration: none;
    text-transform: none;
    display: block;
    word-wrap: normal;
    line-height: 29px;
  }
  & a:hover {
    text-decoration: underline;
  }
`;

interface IStyledHyperlink {
  isIcon?: boolean;
  isFirstElem?: boolean;
}
const StyledHyperlink = styled(Hyperlink)<IStyledHyperlink>`
  display: flex;
  align-items: center;
  height: 66px;
  color: #252024;
  font-size: 14px;
  font-weight: bold;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border: 1px solid transparent;
  border-bottom-color: #d6d6d6;
  transition: all ease-in-out 180ms;

  ${({ isFirstElem }: IStyledHyperlink): string | null =>
    isFirstElem ? `border-top-color: #d6d6d6;` : ""}

  &:hover {
    border-left-color: #d6d6d6;
    border-right-color: #d6d6d6;
  }

  & img {
    padding: 18px 13px;
  }

  ${({ isIcon }: IStyledHyperlink): string =>
    !isIcon
      ? `
  &:before {
    content: "";
    display: block;
    width: 56px;
    height: 66px;
    flex: 0 0 auto;
  }`
      : ""}}
`;

const Image = styled.img`
  max-width: 240px;
  width: 100%;
`;

const GridLi = styled.li`
  float: left;
  display: inline-block;
  width: auto;
  border: none;
  background: none;
  margin: 0px;
  clear: none;
  padding: 0;
  margin-right: 4px;
  margin-bottom: 4px;
`;

const GridHyperlink = styled(Hyperlink)`
  background: #eee;
  line-height: inherit;
  font-size: 13px;
  color: #505459;
  padding: 12px;
  display: block;
  transition: background ease-in-out 180ms;

  &:hover {
    background: #dfdfdf;
    text-decoration: none;
  }
`;

const titleColor = (
  menu: IMenuContentNormed[],
  path: number[]
): { color?: string } => ({
  color: (path.length > 0 && menu[path[0]].backgroundColor) || undefined,
});

export const Links = ({ tab, menu, path }: IProps): JSX.Element | false => (
  <List>
    <Title style={titleColor(menu, path)}>
      <Hyperlink to={tab.href}>{tab.text}</Hyperlink>
    </Title>
    {tab.children &&
      tab.children.length &&
      tab.children.map(({ href, text }: IMenuContentNormed, index: number) => (
        <NestedList key={`Links-${index}`}>
          <Hyperlink to={href}>{text}</Hyperlink>
        </NestedList>
      ))}
  </List>
);

export const IconLinks = ({ tab }: IProps): JSX.Element | null => {
  return (
    <List>
      {tab.children &&
        tab.children.map(
          (
            { href, data: { alt, url }, text }: IMenuContentNormed,
            index: number
          ) => (
            <li key={`IconLinks-${index}`}>
              <StyledHyperlink
                to={href}
                isIcon={!!url}
                isFirstElem={index === 0}
              >
                {url && <img src={url} alt={alt || ""} />}
                {text}
              </StyledHyperlink>
            </li>
          )
        )}
    </List>
  );
};

export const ImageLink = ({ tab, menu, path }: IProps): JSX.Element | null => (
  <List>
    <Title style={titleColor(menu, path)}>
      <Hyperlink to={tab.href}>{tab.text}</Hyperlink>
    </Title>
    <li>
      <Hyperlink to={tab.href}>
        <Image
          src={get(tab, "data.url") || ""}
          alt={get(tab, "data.alt") || ""}
        />
      </Hyperlink>
    </li>
  </List>
);

export const HtmlLink = ({ tab, menu, path }: IProps): JSX.Element | null => (
  <List>
    <Title style={titleColor(menu, path)}>
      <Hyperlink to={tab.href}>{tab.text}</Hyperlink>
    </Title>
    <li
      dangerouslySetInnerHTML={{
        __html: get(tab, "data.0.text") || undefined,
      }}
    ></li>
  </List>
);

export const GridLinks = ({ tab, menu, path }: IProps): JSX.Element | null => (
  <List>
    <Title style={titleColor(menu, path)}>
      <Hyperlink to={tab.href}>{tab.text}</Hyperlink>
    </Title>
    {(tab.children || []).map(
      ({ href, text }: IMenuContentNormed, index: number) => (
        <GridLi key={`GridLinks-${index}`}>
          <GridHyperlink to={href}>{text}</GridHyperlink>
        </GridLi>
      )
    )}
  </List>
);

export const error = (): JSX.Element | null => <p>Slice error.</p>;
